import type { SavedProgression } from "../../hooks/useProgressionLibrary";
import type { NoteName } from "../guitarEngine";
import type { ChordHubCard, ChordHubSection } from "./chordHubTypes";

export const CHORD_HUB_SECTION_LABELS: Record<ChordHubSection, string> = {
  dictionary: "Diccionario",
  progressions: "Progresiones",
  gym: "Entrenamiento",
};

export const CHORD_HUB_STATIC_CARDS: ChordHubCard[] = [
  // ── Diccionario ──
  {
    id: "c-major-open-voicings",
    section: "dictionary",
    title: "Do mayor — 3 voces abiertas",
    subtitle: "Triada mayor · formas clásicas en el mástil",
    description:
      "Explora tres formas habituales de Do mayor en posición abierta y media. Cada voicing colorea la tónica, la tercera y la quinta para que veas la función de cada nota.",
    instructions: [
      "Observa los colores en el mástil: raíz, tercera y quinta.",
      "Recorre las tres formas con las flechas.",
      "Cuando hayas visto las tres, pulsa Completar lección.",
    ],
    tags: ["Triada", "Principiante", "Voces"],
    timeEstimateMin: 3,
    studioKind: "chord-voicing",
    payload: { root: "C", quality: "major", maxVoicings: 3 },
  },
  {
    id: "g-major-open-voicings",
    section: "dictionary",
    title: "Sol mayor — formas abiertas",
    subtitle: "Triada mayor · posición abierta",
    description:
      "Sol mayor es una de las primeras formas que aprenden los guitarristas. Repasa sus voces clásicas y fíjate en la raíz en la 6ª y 3ª cuerda.",
    instructions: [
      "Identifica la raíz (G) en cada forma.",
      "Compara el registro: graves vs agudos.",
      "Completa cuando hayas visto todas las formas.",
    ],
    tags: ["Triada", "Principiante", "Voces"],
    timeEstimateMin: 3,
    studioKind: "chord-voicing",
    payload: { root: "G", quality: "major", maxVoicings: 3 },
  },
  {
    id: "a-minor-open-voicings",
    section: "dictionary",
    title: "La menor — formas abiertas",
    subtitle: "Triada menor · posición abierta",
    description:
      "La menor comparte la forma de Mi mayor desplazada. Observa cómo cambia la tercera (b3) respecto a la mayor.",
    instructions: [
      "Localiza la tercera menor en el mástil.",
      "Recorre las formas con las flechas.",
      "Completa cuando las hayas estudiado todas.",
    ],
    tags: ["Triada", "Menor", "Voces"],
    timeEstimateMin: 3,
    studioKind: "chord-voicing",
    payload: { root: "A", quality: "minor", maxVoicings: 3 },
  },
  {
    id: "e-minor-open-voicings",
    section: "dictionary",
    title: "Mi menor — formas abiertas",
    subtitle: "Triada menor · posición abierta",
    description:
      "Mi menor es la forma más compacta en posición abierta. Ideal para comparar con Mi mayor y sentir la diferencia de una sola nota.",
    instructions: [
      "Compara mentalmente con Mi mayor (E).",
      "Observa qué cuerdas suenan abiertas.",
      "Completa tras ver todas las formas.",
    ],
    tags: ["Triada", "Menor", "Voces"],
    timeEstimateMin: 3,
    studioKind: "chord-voicing",
    payload: { root: "E", quality: "minor", maxVoicings: 3 },
  },
  {
    id: "c-maj7-inversions",
    section: "dictionary",
    title: "Do maj7 — inversiones",
    subtitle: "Tétrada · fundamental y giros",
    description:
      "Recorre las inversiones de Do maj7 en el mástil. Cada giro coloca un grado diferente en el bajo, cambiando la sensación armónica.",
    instructions: [
      "Fíjate qué nota queda en la cuerda más grave.",
      "Pasa de fundamental a 1ª y 2ª inversión.",
      "Completa cuando hayas visto todas las inversiones.",
    ],
    tags: ["Tétrada", "Inversiones", "Jazz"],
    timeEstimateMin: 4,
    studioKind: "chord-inversions",
    payload: { root: "C", quality: "maj7" },
  },
  {
    id: "am-minor-inversions",
    section: "dictionary",
    title: "La menor — inversiones",
    subtitle: "Triada menor · giros en el mástil",
    description:
      "Practica las tres inversiones de La menor. Es el mismo acorde, pero el bajo cambia la percepción de la armonía.",
    instructions: [
      "Identifica la nota del bajo en cada inversión.",
      "Usa las flechas para cambiar de giro.",
      "Completa cuando hayas visto las tres inversiones.",
    ],
    tags: ["Triada", "Inversiones", "Menor"],
    timeEstimateMin: 4,
    studioKind: "chord-inversions",
    payload: { root: "A", quality: "minor" },
  },

  // ── Progresiones preset ──
  {
    id: "prog-pop-IV-V-vi-IV",
    section: "progressions",
    title: "Pop clásico — I V vi IV",
    subtitle: "C · G · Am · F",
    description:
      "La progresión más usada en pop moderno. Escucha el ciclo, sigue cada acorde en el mástil y siente cómo la tensión sube en el V y baja en el IV.",
    instructions: [
      "Pulsa Play y escucha un ciclo completo.",
      "Sigue el acorde activo en el mástil.",
      "Completa cuando hayas oído toda la progresión.",
    ],
    tags: ["Pop", "Triada", "Preset"],
    timeEstimateMin: 4,
    studioKind: "progression-play",
    payload: { progression: ["C", "G", "Am", "F"], key: "C", bpm: 88 },
  },
  {
    id: "prog-jazz-ii-V-I",
    section: "progressions",
    title: "Jazz — ii V I",
    subtitle: "Dm7 · G7 · Cmaj7",
    description:
      "La cadencia jazzística por excelencia. Observa cómo el ii prepara el dominante y este resuelve en la tónica.",
    instructions: [
      "Escucha la tensión en G7 hacia Cmaj7.",
      "Mira la forma de cada acorde en el mástil.",
      "Completa tras un ciclo completo.",
    ],
    tags: ["Jazz", "Tétrada", "Preset"],
    timeEstimateMin: 4,
    studioKind: "progression-play",
    payload: { progression: ["Dm7", "G7", "Cmaj7", "Cmaj7"], key: "C", bpm: 80 },
  },
  {
    id: "prog-rock-I-IV-V",
    section: "progressions",
    title: "Rock — I IV V",
    subtitle: "E · A · B · E",
    description:
      "Power chords y triadas en rock clásico. Tres acordes, máxima energía.",
    instructions: [
      "Reproduce la progresión con Play.",
      "Sigue cada cambio en el mástil.",
      "Completa cuando hayas oído un ciclo.",
    ],
    tags: ["Rock", "Triada", "Preset"],
    timeEstimateMin: 3,
    studioKind: "progression-play",
    payload: { progression: ["E", "A", "B", "E"], key: "E", bpm: 120 },
  },
  {
    id: "prog-blues-12",
    section: "progressions",
    title: "Blues — 12 compases (I IV V)",
    subtitle: "A7 · D7 · E7 · A7",
    description:
      "Esqueleto del blues en La. Dominantes séptimos que definen el género.",
    instructions: [
      "Escucha el groove dominante.",
      "Observa las formas de séptimo en el mástil.",
      "Completa tras un ciclo.",
    ],
    tags: ["Blues", "Dominante", "Preset"],
    timeEstimateMin: 4,
    studioKind: "progression-play",
    payload: { progression: ["A7", "D7", "E7", "A7"], key: "A", bpm: 96 },
  },
  {
    id: "prog-folk-la-bamba",
    section: "progressions",
    title: "Folclore — La Bamba",
    subtitle: "C · F · G · F",
    description:
      "Progresión simple y pegadiza. Ideal para practicar cambios rápidos entre acordes abiertos.",
    instructions: [
      "Pulsa Play y sigue el ritmo.",
      "Anticipa el siguiente acorde mirando la barra de progreso.",
      "Completa tras un ciclo.",
    ],
    tags: ["Folclore", "Triada", "Preset"],
    timeEstimateMin: 3,
    studioKind: "progression-play",
    payload: { progression: ["C", "F", "G", "F"], key: "C", bpm: 120 },
  },

  // ── Gym drills ──
  {
    id: "gym-major-triads",
    section: "gym",
    title: "Tour de triadas mayores",
    subtitle: "C · G · D · A · E",
    description:
      "Recorre cinco triadas mayores fundamentales. Cada parada muestra la forma clásica en el mástil.",
    instructions: [
      "Estudia cada acorde antes de pasar al siguiente.",
      "Fíjate en la raíz y la calidad mayor.",
      "Completa cuando hayas visto los cinco.",
    ],
    tags: ["Triada", "Mayor", "Drill"],
    timeEstimateMin: 5,
    studioKind: "gym-drill",
    payload: {
      drills: [
        { root: "C", quality: "major", label: "Do mayor" },
        { root: "G", quality: "major", label: "Sol mayor" },
        { root: "D", quality: "major", label: "Re mayor" },
        { root: "A", quality: "major", label: "La mayor" },
        { root: "E", quality: "major", label: "Mi mayor" },
      ],
    },
  },
  {
    id: "gym-minor-triads",
    section: "gym",
    title: "Tour de triadas menores",
    subtitle: "Am · Em · Dm · Bm",
    description:
      "Cuatro triadas menores esenciales. Refuerza la diferencia visual respecto a las mayores.",
    instructions: [
      "Localiza la tercera menor en cada forma.",
      "Avanza con las flechas.",
      "Completa cuando hayas visto los cuatro.",
    ],
    tags: ["Triada", "Menor", "Drill"],
    timeEstimateMin: 4,
    studioKind: "gym-drill",
    payload: {
      drills: [
        { root: "A", quality: "minor", label: "La menor" },
        { root: "E", quality: "minor", label: "Mi menor" },
        { root: "D", quality: "minor", label: "Re menor" },
        { root: "B", quality: "minor", label: "Si menor" },
      ],
    },
  },
  {
    id: "gym-pop-chords",
    section: "gym",
    title: "Acordes de pop",
    subtitle: "C · Am · F · G",
    description:
      "Los cuatro acordes que dominan el pop. Practícalos en secuencia como en una canción real.",
    instructions: [
      "Recorre cada acorde con las flechas.",
      "Imagina la progresión I vi IV V.",
      "Completa cuando los hayas visto todos.",
    ],
    tags: ["Pop", "Triada", "Drill"],
    timeEstimateMin: 4,
    studioKind: "gym-drill",
    payload: {
      drills: [
        { root: "C", quality: "major", label: "Do mayor" },
        { root: "A", quality: "minor", label: "La menor" },
        { root: "F", quality: "major", label: "Fa mayor" },
        { root: "G", quality: "major", label: "Sol mayor" },
      ],
    },
  },
  {
    id: "gym-dominant7",
    section: "gym",
    title: "Dominantes séptimos",
    subtitle: "G7 · C7 · D7 · A7",
    description:
      "Cuatro acordes de séptima dominante. Base del blues, jazz y resoluciones V→I.",
    instructions: [
      "Observa la séptima menor (b7) en cada forma.",
      "Compara el tamaño y la extensión en el mástil.",
      "Completa cuando hayas visto los cuatro.",
    ],
    tags: ["Dominante", "Tétrada", "Drill"],
    timeEstimateMin: 4,
    studioKind: "gym-drill",
    payload: {
      drills: [
        { root: "G", quality: "7", label: "Sol 7" },
        { root: "C", quality: "7", label: "Do 7" },
        { root: "D", quality: "7", label: "Re 7" },
        { root: "A", quality: "7", label: "La 7" },
      ],
    },
  },
];

export function getChordHubCardById(id: string): ChordHubCard | undefined {
  return CHORD_HUB_STATIC_CARDS.find((c) => c.id === id);
}

export function getChordHubCardsBySection(section: ChordHubSection): ChordHubCard[] {
  return CHORD_HUB_STATIC_CARDS.filter((c) => c.section === section);
}

export function savedProgressionToHubCard(p: SavedProgression): ChordHubCard {
  const chords = p.progression.filter(Boolean).join(" · ");
  return {
    id: `user-prog-${p.id}`,
    section: "progressions",
    title: p.name,
    subtitle: chords.length > 72 ? `${chords.slice(0, 72)}…` : chords,
    description: `Progresión de tu biblioteca en ${p.key} a ${p.bpm} BPM. Escucha el ciclo y sigue cada acorde en el mástil.`,
    instructions: [
      "Pulsa Play y escucha un ciclo completo.",
      "Sigue el acorde activo en el mástil.",
      "Completa cuando hayas oído toda la progresión.",
    ],
    tags: [...p.tags.slice(0, 2), "Guardada"],
    timeEstimateMin: Math.max(3, Math.ceil(p.progression.filter(Boolean).length / 2)),
    studioKind: "progression-play",
    payload: {
      progression: p.progression,
      key: p.key as NoteName,
      bpm: p.bpm,
      sourceProgressionId: p.id,
    },
  };
}

export function isUserSavedProgression(p: SavedProgression): boolean {
  return !p.id.startsWith("default-");
}

export function getProgressionSectionCards(userProgressions: SavedProgression[]): ChordHubCard[] {
  const staticCards = getChordHubCardsBySection("progressions");
  const userCards = userProgressions.filter(isUserSavedProgression).map(savedProgressionToHubCard);
  return [...staticCards, ...userCards];
}

export function getSectionCards(
  section: ChordHubSection,
  userProgressions: SavedProgression[] = []
): ChordHubCard[] {
  if (section === "progressions") {
    return getProgressionSectionCards(userProgressions);
  }
  return getChordHubCardsBySection(section);
}

export function countHubCards(userProgressions: SavedProgression[] = []): number {
  const userCount = userProgressions.filter(isUserSavedProgression).length;
  return CHORD_HUB_STATIC_CARDS.length + userCount;
}

export function resolveChordHubCard(
  id: string,
  userProgressions: SavedProgression[] = []
): ChordHubCard | undefined {
  const staticCard = getChordHubCardById(id);
  if (staticCard) return staticCard;

  if (id.startsWith("user-prog-")) {
    const progId = id.slice("user-prog-".length);
    const saved = userProgressions.find((p) => p.id === progId);
    if (saved) return savedProgressionToHubCard(saved);
  }

  return undefined;
}
